import type { CustomFieldDefinition } from "@/app/api/types/featureFlags";
import { Input } from "@stockline/ui/components/input";
import { Checkbox } from "@stockline/ui/components/checkbox";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { DateFieldButton } from "@/components/ui/date-field-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@stockline/ui/components/select";
import { useTranslation } from "react-i18next";

type DynamicCustomFieldsProps = {
  fields: CustomFieldDefinition[];
  values: Record<string, string | number | boolean>;
  onChange: (values: Record<string, string | number | boolean>) => void;
  disabled?: boolean;
  /** Compact mode for inline display in tables */
  compact?: boolean;
};

/**
 * Renders dynamic custom fields based on feature flag configuration.
 * Supports text, number, date, select (with clear), and checkbox field types.
 * Values are stored in metadata JSONB columns (flow_metadata, pre_order_metadata).
 */
export function DynamicCustomFields({
  fields,
  values,
  onChange,
  disabled = false,
  compact = false,
}: DynamicCustomFieldsProps) {
  const { t } = useTranslation();

  const handleFieldChange = (key: string, value: string | number | boolean) => {
    onChange({ ...values, [key]: value });
  };

  if (!fields || fields.length === 0) {
    return null;
  }

  // Separate checkboxes from other fields for better layout
  const checkboxFields = fields.filter((f) => f.type === "checkbox");
  const otherFields = fields.filter((f) => f.type !== "checkbox");

  if (compact) {
    // Compact mode: inline fields without labels (for table rows)
    return (
      <div className="flex items-center gap-2">
        {otherFields.map((field) => (
          <div key={field.key} className="flex-shrink-0">
            {renderField(
              field,
              values[field.key],
              handleFieldChange,
              disabled || !!field.readOnly,
              true,
              t,
            )}
          </div>
        ))}
        {checkboxFields.map((field) => (
          <div key={field.key} className="flex items-center space-x-1">
            <Checkbox
              id={`flow-${field.key}`}
              checked={!!values[field.key]}
              onCheckedChange={(checked) =>
                handleFieldChange(field.key, !!checked)
              }
              disabled={disabled || !!field.readOnly}
            />
            <label
              htmlFor={`flow-${field.key}`}
              className="text-xs leading-none cursor-pointer"
            >
              {field.label}
            </label>
          </div>
        ))}
      </div>
    );
  }

  // Full mode: fields with labels (for forms/dialogs)
  return (
    <div className="space-y-3">
      {/* Regular fields in responsive grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {otherFields.map((field) => (
          <div key={field.key} className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {renderField(
              field,
              values[field.key],
              handleFieldChange,
              disabled || !!field.readOnly,
              false,
              t,
            )}
          </div>
        ))}
      </div>

      {/* Checkboxes as bordered field boxes, matching the header field grammar */}
      {checkboxFields.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {checkboxFields.map((field) => (
            <div
              key={field.key}
              className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3"
            >
              <Checkbox
                id={`flow-${field.key}`}
                checked={!!values[field.key]}
                onCheckedChange={(checked) =>
                  handleFieldChange(field.key, !!checked)
                }
                disabled={disabled || !!field.readOnly}
              />
              <label
                htmlFor={`flow-${field.key}`}
                className="text-sm leading-none cursor-pointer select-none"
              >
                {field.label}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** @deprecated Use DynamicCustomFields instead */
export const DynamicPreOrderFlowFields = DynamicCustomFields;

// Sentinel value for clearing a select (Radix Select doesn't allow empty string as value)
const CLEAR_VALUE = "__clear__";

/**
 * The `Input` primitive's chrome, transplanted onto the growing textarea so a
 * multi-line free-text field is indistinguishable from its single-line
 * neighbours until it actually has more than one line to show.
 */
const TEXT_FIELD_CLASS =
  "flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm leading-5 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Parse a stored `YYYY-MM-DD` custom-field value into a local-field `Date`
 * (midnight in the browser's tz). Mirrors how `DatePickerComponent` reads
 * dates, so the calendar day round-trips without timezone drift. `new Date(str)`
 * is deliberately avoided — it parses `YYYY-MM-DD` as UTC and can shift the day.
 */
export function dateStrToLocalDate(
  str: string | number | boolean | undefined | null,
): Date | null {
  // Custom-field values come from untyped JSONB metadata, so a `date` field
  // can hold a non-string (number/boolean). Guard before `.split` to avoid a
  // runtime crash that would blank the whole row.
  if (typeof str !== "string" || !str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Serialize a local-field `Date` back to a bare `YYYY-MM-DD` calendar day. */
export function localDateToDateStr(date: Date | null): string {
  if (!date || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function renderField(
  field: CustomFieldDefinition,
  value: string | number | boolean | undefined,
  onChange: (key: string, value: string | number | boolean) => void,
  disabled: boolean,
  compact: boolean,
  t: (key: string, fallback: string) => string,
) {
  // For compact mode: text/number inputs are narrow, selects are wider to show options
  const inputClass = compact ? "h-7 text-xs w-24" : "h-8 text-sm";
  const selectClass = compact
    ? "h-7 text-xs w-full min-w-[140px]"
    : "h-8 text-sm";

  switch (field.type) {
    case "text":
      // Free text has no natural length, and a one-line box silently hides
      // whatever doesn't fit — the exact failure this form exists to avoid on
      // a header block the user reads at a glance. Compact mode keeps the
      // one-line input: it lives in a fixed-width table slot that has nowhere
      // to grow. `.fill()` and the testid work identically on a textarea.
      if (compact) {
        return (
          <Input
            type="text"
            data-testid={`custom-field-input-${field.key}`}
            placeholder={field.label}
            value={(value as string) || ""}
            onChange={(e) => onChange(field.key, e.target.value)}
            disabled={disabled}
            className={inputClass}
          />
        );
      }
      return (
        <AutoGrowTextarea
          data-testid={`custom-field-input-${field.key}`}
          value={(value as string) || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={disabled}
          minHeight={32}
          maxHeight={200}
          className={TEXT_FIELD_CLASS}
        />
      );

    case "number":
      return (
        <Input
          type="number"
          step="any"
          data-testid={`custom-field-input-${field.key}`}
          placeholder={compact ? field.label : undefined}
          value={value !== undefined && value !== "" ? String(value) : ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange(field.key, "");
            } else {
              const parsed = parseFloat(raw);
              onChange(field.key, Number.isNaN(parsed) ? "" : parsed);
            }
          }}
          disabled={disabled}
          className={inputClass}
        />
      );

    case "date":
      // Trigger button + popover calendar (matches the order/delivery date
      // control), instead of a native <input type="date">. The stored value
      // stays a bare `YYYY-MM-DD` calendar day — convert to/from a local-field
      // Date so there is no timezone drift.
      return (
        <DateFieldButton
          data-testid={`custom-field-input-${field.key}`}
          value={dateStrToLocalDate(value)}
          onChange={(date) => onChange(field.key, localDateToDateStr(date))}
          disabled={disabled}
          compact={compact}
          placeholder={field.label}
        />
      );

    case "select": {
      // In compact mode, show abbreviated value in trigger
      const selectedOption = field.options?.find(
        (opt) => opt.value === (value as string),
      );

      // Smart abbreviation for compact display
      let displayValue = undefined;
      if (compact && selectedOption) {
        const label = selectedOption.label;
        const maxLength = 20; // Max characters before truncation

        if (label.length <= maxLength) {
          // Short enough, show as-is
          displayValue = label;
        } else {
          // Long label: try intelligent abbreviation
          // Strategy 1: If has separator like " - ", show code + first word after separator
          const dashMatch = label.match(/^([^-]+)\s*-\s*(.+)$/);
          if (dashMatch) {
            const [, code, rest] = dashMatch;
            const firstWord = rest.trim().split(/[\s/]+/)[0];
            displayValue = `${code.trim()}-${firstWord}`;
          } else {
            // Strategy 2: Take first N chars + ellipsis
            displayValue = label.substring(0, maxLength - 1) + "…";
          }
        }
      }

      return (
        <Select
          value={(value as string) || CLEAR_VALUE}
          onValueChange={(v) => {
            if (v === CLEAR_VALUE) {
              onChange(field.key, "");
            } else {
              onChange(field.key, v);
            }
          }}
          disabled={disabled}
        >
          <SelectTrigger className={selectClass}>
            {displayValue ? (
              <span
                className="break-all line-clamp-1"
                title={selectedOption?.label}
              >
                {displayValue}
              </span>
            ) : (
              <SelectValue placeholder={compact ? field.label : "-"} />
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CLEAR_VALUE} className="text-muted-foreground">
              {t("common.none", "None")}
            </SelectItem>
            {field.options
              ?.filter((option) => !!option.value)
              .map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      );
    }

    default:
      return null;
  }
}
